import { useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { DropResult } from "@hello-pangea/dnd";

import useGetQuestionLists from "../../hooks/useGetQuestionLists";
import { getToday } from "tools/functions/Methods";
import { addEditDataAPI } from "services/CRUDServices";

import { Question, SelectQuestionItem } from "../types";
import { generateQuestionInEditMode, generateQuestionLists } from "../utils";
import { surveyValidation } from "global/Validation/Validation";
import { useNavigate } from "react-router-dom";

interface UseSurveyFormArgs {
  currentData: any;
  questionBankOpen: boolean;
  setOpen: (val: boolean) => void;
  refreshTable: () => void;
}


export function useSurveyForm({
  currentData,
  questionBankOpen,
  setOpen,
  refreshTable,
}: UseSurveyFormArgs) {
  const { questions, loading: questionLoading } = useGetQuestionLists(
    questionBankOpen,
    currentData
  );

  const navigate=useNavigate()
  const formik = useFormik({
    enableReinitialize: true,
     validationSchema: surveyValidation,
    initialValues: {
      id: currentData?.id ?? null,
      isActive: currentData?.isActive ?? true,
      title: currentData?.title ?? "",
      validFrom: currentData?.validFrom ?? getToday(true),
      validTo: currentData?.validTo ?? getToday(true),
      description: currentData?.description ?? "",
      version: currentData?.version ?? "",
      displayOrder: currentData?.displayOrder ?? { id: 0, text: "" },
      selectQuestionList: generateQuestionInEditMode(
        currentData?.questionInstanceList ?? []
      ) as SelectQuestionItem[],
      surveyType: currentData?.selectSurveyType ?? { id: 2, text: "هر دو" },
    },
    onSubmit: async (values) => {
      try {
        const finalValues = {
          id: values.id ?? null,
          surveyType: values.surveyType?.id ?? 1,
          isActive: values.isActive,
          title: values.title,
          validFrom: values.validFrom,
          validTo: values.validTo,
          description: values.description,
          version: values.version,
          displayOrder: values.displayOrder ?? { id: 0, text: "" },
          questionList: generateQuestionLists(
            values.selectQuestionList,
            questions
          ),
        };

        await addEditDataAPI(
          currentData
            ? "/customer-api/survey/s1/v2/edit"
            : "/customer-api/survey/s1/v2/create",
          currentData?.id ? "put" : "post",
          finalValues
        );


    
        toast.success("عملیات با موفقیت انجام شد");
        navigate("/basic-information/survey")
      } catch (error) {
        console.error(error);
  
      }
    },
  });

  const { values, setFieldValue } = formik;

  const dependencyState = useMemo(() => {
    const map: Record<
      number,
      {
        parentQuestionId: number;
        contentId: number[];
        isRequired: boolean;
        selectedPoint?: number;
      }
    > = {};

    (values.selectQuestionList || []).forEach((item: SelectQuestionItem) => {
      map[item.questionId] = {
        parentQuestionId: item.parentQuestionId ?? 0,
        contentId: item.contentId ?? [],
        isRequired: item.isRequired ?? false,
        selectedPoint: item.selectedPoint ?? 0,
      };
    });

    return map;
  }, [values.selectQuestionList]);

  const selectedQuestions = useMemo(() => {
    return (values.selectQuestionList || [])
      .map((item: SelectQuestionItem) =>
        questions.find((q: Question) => q.id === item.questionId)
      )
      .filter(Boolean) as Question[];
  }, [values.selectQuestionList, questions]);

  const availableQuestions = useMemo(() => {
    const selectedIds = new Set(
      (values.selectQuestionList || []).map(
        (item: SelectQuestionItem) => item.questionId
      )
    );

    const selectedTypeId = values.surveyType?.id;
    const isBothType = selectedTypeId === 2;

    return (questions || []).filter((q: Question) => {
      if (selectedIds.has(q.id)) return false;

      if (
        selectedTypeId === undefined ||
        selectedTypeId === null ||
        isBothType
      ) {
        return true;
      }
      return q.questionType?.id === selectedTypeId;
    });
  }, [questions, values.selectQuestionList, values.surveyType]);

  const cleanDependencyList = (
    list: SelectQuestionItem[]
  ): SelectQuestionItem[] => {
    return list.map((item, index, arr) => {
      const previousQuestionIds = arr.slice(0, index).map((q) => q.questionId);
      const parentIsPrevious = previousQuestionIds.includes(
        item.parentQuestionId
      );

      if (item.parentQuestionId > 0 && !parentIsPrevious) {
        return {
          ...item,
          parentQuestionId: 0,
          contentId: [],
        };
      }

      return item;
    });
  };

  const addQuestionToSelected = (question: Question) => {
    const exists = (values.selectQuestionList || []).some(
      (item: SelectQuestionItem) => item.questionId === question.id
    );

    if (exists) return;

    const newList = [
      ...(values.selectQuestionList || []),
      {
        questionId: question.id,
        parentQuestionId: 0,
        contentId: [],
        isRequired: false,
        selectedPoint: 0,
      },
    ];

    setFieldValue("selectQuestionList", cleanDependencyList(newList));
  };

  const handleDependencyChange = (
    questionId: number,
    parentQuestionId: number,
    contentId: number[],
    selectedPoint?: number
  ) => {
    const newList = (values.selectQuestionList || []).map(
      (item: SelectQuestionItem) =>
        item.questionId === questionId
          ? {
              ...item,
              parentQuestionId,
              contentId,
              selectedPoint,
            }
          : item
    );

    setFieldValue("selectQuestionList", newList);
  };

  const handleRequiredChange = (questionId: number, required: boolean) => {
    const newList = (values.selectQuestionList || []).map(
      (item: SelectQuestionItem) =>
        item.questionId === questionId
          ? {
              ...item,
              isRequired: required,
            }
          : item
    );

    setFieldValue("selectQuestionList", newList);
  };

  const handleDeleteQuestion = (id: number) => {
    let newList = (values.selectQuestionList || []).filter(
      (item: SelectQuestionItem) => item.questionId !== id
    );

    newList = newList.map((item: SelectQuestionItem) => {
      if (item.parentQuestionId === id) {
        return {
          ...item,
          parentQuestionId: 0,
          contentId: [],
        };
      }
      return item;
    });

    newList = cleanDependencyList(newList);
    setFieldValue("selectQuestionList", newList);
  };

  const handleDeleteAll = () => {
    setFieldValue("selectQuestionList", []);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    if (
      source.droppableId === "question-bank" &&
      destination.droppableId === "selected-questions"
    ) {
      const questionId = Number(draggableId.replace("bank-", ""));
      const questionToAdd = questions.find(
        (q: Question) => q.id === questionId
      );

      if (!questionToAdd) return;

      const alreadyExists = (values.selectQuestionList || []).some(
        (item: SelectQuestionItem) => item.questionId === questionId
      );

      if (alreadyExists) return;

      const updated = [...(values.selectQuestionList || [])];
      updated.splice(destination.index, 0, {
        questionId: questionToAdd.id,
        parentQuestionId: 0,
        contentId: [],
        isRequired: false,
        selectedPoint: 0,
      });

      setFieldValue("selectQuestionList", cleanDependencyList(updated));
      return;
    }

    if (
      source.droppableId === "selected-questions" &&
      destination.droppableId === "selected-questions"
    ) {
      const updated = [...(values.selectQuestionList || [])];
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);

      setFieldValue("selectQuestionList", cleanDependencyList(updated));
    }
  };

  return {
    formik,
    questions,
    questionLoading,
    dependencyState,
    selectedQuestions,
    availableQuestions,
    addQuestionToSelected,
    handleDependencyChange,
    handleRequiredChange,
    handleDeleteQuestion,
    handleDeleteAll,
    onDragEnd,
  };
}
